/** 샵관리 — 서비스마스터. 1회 표준원가·사용제품 요약은 「서비스 사용제품」(BOM) 과 현재 FIFO 단가로 자동 계산 */
import React from 'react';
import { hiCol } from 'components/grid/hiGrid';
import ShopGridPage from './ShopGridPage';
import {
  loadTable, saveTable, persistTable, createUid, nextCode, num, serviceStdCost, serviceItemsOf,
} from 'shop/shopStore';
import { kwField, ynField, matchKeyword, dupErrors } from 'shop/shopPageHelpers';

const DERIVED = ['stdCost', 'margin', 'itemSummary'];

const filter = (rows, v) =>
  rows.filter(
    (r) => r.mod !== 'D'
      && (!v.useYn || r.useYn === v.useYn)
      && matchKeyword(r, ['serviceCode', 'serviceNm', 'category'], v.keyword)
  );

const cfg = {
  gridId: 'sh-service',
  title: '서비스마스터',
  uidKey: 'serviceUid',
  searchDefaults: { keyword: '', useYn: 'Y' },
  searchFields: () => [[kwField('코드·서비스명·분류'), ynField()]],
  columns: () => [
    hiCol.edTextColumnForKey({ header: '서비스코드', field: 'serviceCode', size: 110, required: true }),
    hiCol.edTextColumn({ header: '서비스명', field: 'serviceNm', size: 200, required: true }),
    hiCol.edTextColumn({ header: '분류', field: 'category', size: 100 }),
    hiCol.edNumberColumn({ header: '판매단가(1회)', field: 'salePrice', size: 110 }),
    hiCol.edNumberColumn({ header: '표준횟수', field: 'stdCnt', size: 80, title: '패키지 기본 횟수' }),
    hiCol.edNumberColumn({ header: '소요(분)', field: 'durationMin', size: 80 }),
    hiCol.edCheckboxColumn({ header: '정기결제', field: 'recurringYn', size: 84 }),
    hiCol.roNumberColumn({ header: '1회 표준원가', field: 'stdCost', size: 110 }),
    hiCol.roNumberColumn({ header: '1회 마진', field: 'margin', size: 100 }),
    hiCol.roTextColumn({ header: '사용제품', field: 'itemSummary', size: 260 }),
    hiCol.edCheckboxColumn({ header: '사용', field: 'useYn', size: 72 }),
    hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
  ],
  load: (tc) => {
    const products = new Map(loadTable(tc, 'product').map((p) => [p.productUid, p]));
    return loadTable(tc, 'service').map((s) => {
      const stdCost = serviceStdCost(tc, s.serviceUid);
      return {
        ...s,
        mod: '',
        stdCost,
        margin: num(s.salePrice) - stdCost,
        itemSummary: serviceItemsOf(tc, s.serviceUid)
          .map((i) => `${products.get(i.productUid)?.productNm || '-'} ${num(i.useQty)}${products.get(i.productUid)?.useUnit || ''}`)
          .join(', '),
      };
    });
  },
  filter,
  emptyRow: (rows) => ({
    serviceUid: createUid('SVC'),
    serviceCode: nextCode(rows, 'serviceCode', 'S'),
    serviceNm: '',
    category: '',
    salePrice: 0,
    stdCnt: 1,
    durationMin: 60,
    recurringYn: 'N',
    useYn: 'Y',
    remark: '',
  }),
  validate: (rows, tc) => {
    const errors = dupErrors(rows, 'serviceCode', '서비스코드');
    const used = new Set([
      ...loadTable(tc, 'pass').map((p) => p.serviceUid),
      ...loadTable(tc, 'record').map((r) => r.serviceUid),
    ]);
    for (const r of rows) {
      if (r.mod === 'D') {
        if (used.has(r.serviceUid)) errors.push(`회원권·시술 이력이 있는 서비스는 삭제할 수 없습니다(사용=N 처리): ${r.serviceNm}`);
        continue;
      }
      if (!String(r.serviceCode || '').trim()) errors.push('서비스코드는 필수입니다.');
      if (!String(r.serviceNm || '').trim()) errors.push(`서비스명 누락 (${r.serviceCode})`);
    }
    return errors;
  },
  save: (tc, rows, v) => {
    const removed = rows.filter((r) => r.mod === 'D').map((r) => r.serviceUid);
    persistTable(tc, 'service', 'serviceUid', rows, (r) => filter([r], v).length > 0, (r) => {
      const next = {
        ...r,
        serviceCode: String(r.serviceCode).trim(),
        serviceNm: String(r.serviceNm).trim(),
        salePrice: num(r.salePrice),
        stdCnt: num(r.stdCnt) || 1,
        durationMin: num(r.durationMin),
        recurringYn: r.recurringYn === 'Y' ? 'Y' : 'N',
        useYn: r.useYn === 'N' ? 'N' : 'Y',
      };
      DERIVED.forEach((k) => delete next[k]);
      return next;
    });
    if (removed.length) {
      const gone = new Set(removed);
      const items = loadTable(tc, 'serviceItem').filter((i) => !gone.has(i.serviceUid));
      saveTable(tc, 'serviceItem', items);
    }
  },
};

const ServiceMaster = () => <ShopGridPage cfg={cfg} />;
export default ServiceMaster;
